import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { ArrowUpRight, Phone, MapPin } from "lucide-react";
import { MaskHeading, EASE } from "@/components/site/motion";
import { BUSINESS } from "@/config/businessConfig";

const RotatingBadge = () => (
  <div className="hidden sm:block absolute -left-10 -bottom-10 lg:-left-14 lg:-bottom-14 w-28 h-28 lg:w-36 lg:h-36 z-20" aria-hidden="true">
    <svg viewBox="0 0 100 100" className="w-full h-full spin-slow">
      <defs><path id="circ" d="M50,50 m-38,0 a38,38 0 1,1 76,0 a38,38 0 1,1 -76,0" /></defs>
      <circle cx="50" cy="50" r="49" fill="#0F1012" stroke="#FF5A1F" strokeWidth="1" />
      <text fill="#fff" fontSize="9.5" fontFamily="JetBrains Mono" letterSpacing="2.2">
        <textPath href="#circ">ASSEMBLE · INSTALL · MOUNT ·</textPath>
      </text>
    </svg>
    <div className="absolute inset-0 grid place-items-center">
      <svg viewBox="0 0 32 32" className="w-9 h-9 lg:w-11 lg:h-11"><path d="M16 1.5 28.6 8.75v14.5L16 30.5 3.4 23.25V8.75Z" fill="#FF5A1F" /><circle cx="16" cy="16" r="5" fill="#0F1012" /></svg>
    </div>
  </div>
);

const HeroFrame = ({ scrollY }) => {
  const ref = useRef(null);
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rx = useSpring(useTransform(my, [0, 1], [7, -7]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [0, 1], [-9, 9]), { stiffness: 120, damping: 18 });
  const imgY = useTransform(scrollY, [0, 1], ["0%", "18%"]);
  const spot = useTransform([mx, my], ([x, y]) => `radial-gradient(circle at ${x * 100}% ${y * 100}%, rgba(255,90,31,0.0) 0%, rgba(15,16,18,0.15) 22%, rgba(15,16,18,0.78) 60%)`);

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  };
  const reset = () => { mx.set(0.5); my.set(0.5); };

  return (
    <div className="relative">
    <motion.div
      initial={{ opacity: 0, clipPath: "inset(100% 0 0 0)" }}
      animate={{ opacity: 1, clipPath: "inset(0% 0 0 0)" }}
      transition={{ duration: 1.4, ease: EASE, delay: 0.35 }}
      className="relative" style={{ perspective: 1200 }}
    >
      <motion.div ref={ref} onMouseMove={onMove} onMouseLeave={reset} data-testid="hero-image-frame"
        style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
        className="relative clip-cut aspect-[4/5] sm:aspect-[5/4] lg:aspect-[4/5] w-full bg-[var(--steel)] overflow-hidden">
        <motion.img src="/img/hero.jpg" alt="Technician assembling furniture with a cordless drill in Woodstock, Ontario"
          style={{ y: imgY, scale: 1.18 }} className="absolute inset-0 w-full h-full object-cover" fetchpriority="high" />
        <motion.div className="absolute inset-0" style={{ background: spot }} />
        <div className="absolute top-5 left-5 right-5 flex justify-between font-mono-x text-[11px] uppercase tracking-[0.18em] text-white/90">
          <span className="bg-[var(--ink)]/70 backdrop-blur px-2.5 py-1">Spec: 01 / Assembly</span>
          <span className="bg-[var(--orange)] text-[var(--ink)] px-2.5 py-1 font-bold">On-site</span>
        </div>
        <div className="absolute bottom-5 right-5 left-5 sm:left-auto bg-[rgba(15,16,18,0.7)] backdrop-blur-xl border border-white/10 p-4 sm:max-w-[260px]" style={{ transform: "translateZ(60px)" }}>
          <p className="font-display text-2xl font-black uppercase leading-none">Box to built.</p>
          <p className="text-sm text-zinc-300 mt-1.5">Furniture, BBQs, sheds, gym gear, TV mounts & more.</p>
        </div>
      </motion.div>
    </motion.div>
    <RotatingBadge />
    </div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", typeof window !== "undefined" && window.innerWidth >= 1024 ? "25%" : "0%"]);

  return (
    <section ref={ref} className="relative min-h-[100svh] bg-[var(--ink)] blueprint grain overflow-hidden pt-28 lg:pt-32 pb-16" data-testid="hero-section">
      <div className="absolute -top-40 -left-40 w-[640px] h-[640px] rounded-full bg-[var(--orange)] opacity-[0.10] blur-[140px] pointer-events-none" />
      <div className="relative max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-12 grid gap-14 lg:grid-cols-12 items-center">
        <motion.div style={{ y: textY }} className="lg:col-span-7 relative z-10">
          <motion.p initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, ease: EASE }}
            className="eyebrow flex items-center gap-3 mb-7">
            <span className="w-10 h-px bg-[var(--orange)]" /><MapPin className="w-3.5 h-3.5" /> Woodstock · Ingersoll · Oxford County
          </motion.p>
          <MaskHeading as="h1" onLoad testId="hero-headline"
            className="font-display font-black uppercase leading-[0.86] tracking-[-0.01em] whitespace-nowrap text-[12.2vw] sm:text-[11vw] lg:text-[min(7vw,7.4rem)]"
            lines={[
              { text: "You Buy It." },
              { text: "We Assemble It.", className: "text-[var(--orange)]" },
              { text: "We Install It." },
            ]} />
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE, delay: 0.75 }}>
            <p className="mt-8 max-w-xl text-base lg:text-lg text-zinc-300 leading-relaxed">
              Woodstock's go-to crew for assembly and installation. Furniture, BBQs, gazebos, sheds, fitness equipment, TV mounts, blinds, shelving and gates — practically anything that comes in a box.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <Link to="/contact" data-testid="hero-quote-btn" className="btn-orange justify-center px-8 py-4 text-lg"><span>Get a Free Quote</span><ArrowUpRight className="w-5 h-5" /></Link>
              <a href={BUSINESS.phoneHref} data-testid="hero-call-btn" className="btn-ghost justify-center px-8 py-4 text-lg"><Phone className="w-4 h-4" /> Call {BUSINESS.phoneDisplay}</a>
            </div>
          </motion.div>
        </motion.div>
        <div className="lg:col-span-5 relative px-4 sm:px-10 lg:px-0">
          <HeroFrame scrollY={scrollYProgress} />
        </div>
      </div>
    </section>
  );
};
